"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import {
  ArrowRight,
  ExternalLink,
  RotateCcw,
  CheckCircle,
  XCircle,
  BarChart2,
  Shield,
  Sparkles,
  Mail,
  ChevronDown,
  ChevronUp,
  FileText,
  Settings,
  Activity,
  Plug,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ContentTypeGroup {
  type: string;
  pattern: string;
  count: number;
}

interface SitemapData {
  totalPages: number;
  contentTypes: ContentTypeGroup[];
  recommendedPlan: string;
  recommendedPrice: number | null;
  averageUpdateFrequency?: string;
  lastModified?: string;
  source?: string;
}

interface InfrastructureInfo {
  isCloudflare: boolean;
  dnsProvider: string;
  deploymentPath: "byoc" | "cname";
  nameservers: string[];
}

interface PlatformInfo {
  cms:
    | "wordpress"
    | "woocommerce"
    | "shopify"
    | "drupal"
    | "squarespace"
    | "wix"
    | "webflow"
    | "ghost"
    | "unknown";
  evidence: string;
  hasPlugin: boolean;
}

interface LlmChecks {
  llmsTxt: {
    found: boolean;
    path: string | null;
    statusCode: number | null;
    sectionCount: number;
    entryCount: number;
  };
  llmsFullTxt: {
    found: boolean;
    path: string | null;
    statusCode: number | null;
    lineCount: number;
    contentLength: number;
  };
  aiSitemap: {
    found: boolean;
    url: string;
    statusCode: number | null;
    isValidJson: boolean;
    itemCount: number;
  };
  markdown: {
    url: string;
    statusCode: number | null;
    servesMarkdown: boolean;
    contentType: string | null;
    hasVaryAccept: boolean;
    hasContentSignal: boolean;
    hasXRobotsTag: boolean;
  };
  robots: {
    found: boolean;
    blockedCrawlerNames: string[];
    allowedCrawlerNames: string[];
    perCrawler: Array<{
      crawler: string;
      allowed: boolean;
      directives: string[];
    }>;
  };
  discovery: {
    hasAlternateMarkdown: boolean;
    hasAiIndexLink: boolean;
  };
  pagePolicy: {
    hasRobotsMeta: boolean;
    robotsContent: string | null;
    blocksIndexing: boolean;
    blocksFollowing: boolean;
  };
  structuredData: {
    jsonLdCount: number;
    hasJsonLd: boolean;
  };
  freshness: {
    hasLastModified: boolean;
    hasEtag: boolean;
    cacheControl: string | null;
  };
  metadata: {
    hasTitle: boolean;
    hasMetaDescription: boolean;
    hasCanonical: boolean;
  };
  semantics: {
    hasMain: boolean;
    hasArticle: boolean;
    hasH1: boolean;
  };
  summary: {
    score: number;
    maxScore: number;
    scoreLabel: string;
    scoreBand: "poor" | "needs-work" | "good" | "strong";
    checksPassed: number;
    checksTotal: number;
    findings: string[];
    strengths: string[];
    blockedCrawlerNames: string[];
    markdownReady: boolean;
    llmsTxtReady: boolean;
    llmsFullTxtReady: boolean;
    aiSitemapReady: boolean;
    jsShellRisk: boolean;
  };
}

interface Phase1Data {
  url: string;
  domain: string;
  title: string;
  sitemap: SitemapData | null;
  infrastructure: InfrastructureInfo | null;
  platform: PlatformInfo | null;
  llmChecks: LlmChecks | null;
}

interface Phase2Data {
  markdown: string;
  markdownFull?: string;
  title?: string;
  stats: {
    htmlTokens: number;
    markdownTokens: number;
    reductionPercent: number;
  };
  isJsRendered: boolean;
  rawContentLength: number;
  markdownContentLength: number;
  llmChecks?: LlmChecks | null;
}

type SignalStatus = "ready" | "partial" | "missing";

interface SignalCheckItem {
  label: string;
  status: SignalStatus;
  detail: string;
  href: string;
}

// ─── Plan config ──────────────────────────────────────────────────────────────

const PLAN_LABELS: Record<string, string> = {
  free: "Free",
  lite: "Lite",
  starter: "Starter",
  pro: "Pro",
  business: "Business",
  scale: "Scale",
  enterprise: "Enterprise",
};

const PLAN_PRICES: Record<string, number | null> = {
  free: 0,
  lite: 19,
  starter: 49,
  pro: 149,
  business: 399,
};

// ─── Small reusable components ────────────────────────────────────────────────

const CMS_LABELS: Record<string, string> = {
  wordpress: "WordPress site",
  woocommerce: "WooCommerce store",
  shopify: "Shopify store",
  drupal: "Drupal site",
  squarespace: "Squarespace site",
  wix: "Wix site",
  webflow: "Webflow site",
  ghost: "Ghost blog",
  unknown: "",
};

function PlatformBadge({ cms }: { cms: string }) {
  const label = CMS_LABELS[cms];
  if (!label) return null;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-500/15 text-indigo-300 border border-indigo-500/20">
      <Sparkles className="w-3 h-3" />
      {label}
    </span>
  );
}

function CloudflareBadge() {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-orange-500/10 text-orange-300/80 border border-orange-500/15">
      <Shield className="w-3 h-3" />
      Protected by Cloudflare
    </span>
  );
}

function SkeletonLine({ w, h = 14 }: { w: string; h?: number }) {
  return (
    <div
      className="rounded bg-white/10 animate-pulse"
      style={{ width: w, height: h }}
    />
  );
}

// ─── URL re-analyze bar ───────────────────────────────────────────────────────

function UrlAnalyzerBar({ defaultUrl }: { defaultUrl: string }) {
  const router = useRouter();
  const [input, setInput] = useState(defaultUrl);
  const [error, setError] = useState("");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    let val = input.trim();
    if (!val) return;
    if (!val.startsWith("http://") && !val.startsWith("https://")) val = `https://${val}`;
    try { new URL(val); } catch { setError("Enter a valid URL."); return; }
    setError("");
    router.push(`/preview?url=${encodeURIComponent(val)}`);
  }

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-2xl mx-auto">
      <div className="flex gap-2 flex-col sm:flex-row">
        <input
          type="url"
          value={input}
          onChange={(e) => { setInput(e.target.value); setError(""); }}
          placeholder="https://yoursite.com"
          className="flex-1 px-4 py-3 rounded-lg bg-white border border-gray-200 text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="submit"
          className="px-5 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-500 transition-colors text-sm whitespace-nowrap"
        >
          Analyze &rarr;
        </button>
      </div>
      {error && <p className="mt-1.5 text-red-400 text-sm">{error}</p>}
    </form>
  );
}

// ─── Info bar (two-column layout) ─────────────────────────────────────────────

function InfoBar({
  phase1,
  phase2,
  phase2Loading,
  phase2Error,
}: {
  phase1: Phase1Data;
  phase2: Phase2Data | null;
  phase2Loading: boolean;
  phase2Error: string | null;
}) {
  const { sitemap, platform, infrastructure } = phase1;
  const knownTotal = sitemap?.contentTypes.reduce((s, g) => s + g.count, 0) ?? 0;
  const otherCount = sitemap ? sitemap.totalPages - knownTotal : 0;

  return (
    <div className="rounded-2xl border border-blue-500/20 bg-gradient-to-r from-blue-500/[0.08] to-blue-600/[0.03] p-6 shadow-[0_0_60px_rgba(59,130,246,0.08)] backdrop-blur-sm">
      <div className="flex flex-col md:flex-row gap-6 md:items-stretch">

        {/* Left: page count, badges, breakdown */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-3">
            <BarChart2 className="w-5 h-5 text-blue-400 shrink-0" />
            <h3 className="font-semibold text-white text-lg leading-tight">
              {sitemap
                ? <><span className="text-blue-400">{sitemap.totalPages.toLocaleString()} pages</span> found</>
                : <span className="text-blue-400">{phase1.domain}</span>
              }
            </h3>
          </div>

          <div className="flex flex-wrap gap-2 mb-3">
            {platform && platform.cms !== "unknown" && <PlatformBadge cms={platform.cms} />}
            {infrastructure?.isCloudflare && <CloudflareBadge />}
          </div>

          {sitemap && sitemap.contentTypes.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {sitemap.contentTypes.map(({ type, count }) => (
                <span
                  key={type}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/8 border border-white/10 text-xs"
                >
                  <span className="text-white/70 font-medium">{type}</span>
                  <span className="text-blue-300 font-semibold tabular-nums">{count.toLocaleString()}</span>
                </span>
              ))}
              {otherCount > 0 && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-xs">
                  <span className="text-white/40 font-medium">Other</span>
                  <span className="text-blue-300/50 font-semibold tabular-nums">{otherCount.toLocaleString()}</span>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="hidden md:block w-px bg-white/10 self-stretch" />
        <div className="block md:hidden h-px bg-white/10 w-full" />

        {/* Right: hero stat — noise ratio (Mode A) or invisibility warning (Mode B) */}
        <div className="flex flex-col items-center justify-center md:w-52 text-center py-2 shrink-0">
          {phase2Loading && !phase2Error ? (
            <>
              <div className="text-5xl font-extrabold tabular-nums leading-none text-blue-400/25 animate-pulse">
                —%
              </div>
              <div className="text-sm text-gray-500 mt-2">noise</div>
              <p className="text-xs text-gray-700 mt-2 animate-pulse">Analyzing…</p>
            </>
          ) : phase2?.isJsRendered ? (
            <>
              <div className="text-4xl font-extrabold leading-none text-amber-400">Invisible</div>
              <div className="text-sm text-gray-300 font-medium mt-2">to AI crawlers</div>
              <div className="text-xs text-gray-500 mt-1">Your site needs JS to render &mdash;</div>
              <div className="text-xs text-gray-500">AI can&apos;t read it at all</div>
            </>
          ) : phase2 ? (
            <>
              <div className="text-5xl font-black tabular-nums leading-none text-transparent bg-clip-text bg-gradient-to-b from-blue-300 to-blue-500 drop-shadow-[0_0_15px_rgba(59,130,246,0.4)]">
                {phase2.stats.reductionPercent}%
              </div>
              <div className="text-sm text-gray-300 font-medium mt-2">of this page is noise</div>
              <div className="text-xs text-gray-500 mt-1">that AI has to wade through</div>
              <div className="text-xs text-gray-500">before reaching your content</div>
            </>
          ) : (
            <>
              <div className="text-4xl font-extrabold tabular-nums leading-none text-gray-600">—</div>
              <div className="text-xs text-gray-600 mt-2">preview unavailable</div>
            </>
          )}
        </div>

      </div>
    </div>
  );
}

// ─── Info bar skeleton (phase 1 loading) ─────────────────────────────────────

function InfoBarSkeleton() {
  return (
    <div className="rounded-2xl border border-blue-500/20 bg-blue-500/5 p-6">
      <div className="flex flex-col md:flex-row gap-6 md:items-stretch">
        <div className="flex-1 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded bg-white/10 animate-pulse" />
            <SkeletonLine w="45%" h={20} />
          </div>
          <div className="flex gap-2">
            <SkeletonLine w="100px" h={26} />
            <SkeletonLine w="130px" h={26} />
          </div>
          <SkeletonLine w="60%" h={14} />
        </div>
        <div className="hidden md:block w-px bg-white/10 self-stretch" />
        <div className="flex flex-col items-center justify-center md:w-44 text-center gap-2 py-2">
          <div className="text-5xl font-extrabold tabular-nums leading-none text-blue-400/20 animate-pulse">—%</div>
          <SkeletonLine w="80px" h={12} />
          <SkeletonLine w="100px" h={10} />
        </div>
      </div>
    </div>
  );
}

// ─── Comparison cards skeleton ────────────────────────────────────────────────

function ComparisonSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="rounded-2xl border border-red-500/20 bg-red-500/5 p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-2 h-2 rounded-full bg-red-400/30" />
          <SkeletonLine w="120px" h={12} />
        </div>
        <div className="space-y-3 mb-5">
          {[80, 60, 70, 55].map((w, i) => (
            <div key={i} className="flex justify-between">
              <SkeletonLine w="55px" h={12} />
              <SkeletonLine w={`${w}px`} h={12} />
            </div>
          ))}
        </div>
        <div className="rounded-lg bg-black/30 p-3 h-44 space-y-2">
          {[90, 70, 80, 55, 75, 60].map((w, i) => (
            <SkeletonLine key={i} w={`${w}%`} h={10} />
          ))}
        </div>
        <p className="text-xs text-gray-700 text-center mt-3 animate-pulse">Analyzing content…</p>
      </div>

      <div className="rounded-2xl border border-blue-500/20 bg-blue-500/5 p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-2 h-2 rounded-full bg-blue-400/30" />
          <SkeletonLine w="90px" h={12} />
        </div>
        <div className="space-y-3 mb-5">
          {[60, 55, 85, 40].map((w, i) => (
            <div key={i} className="flex justify-between">
              <SkeletonLine w="55px" h={12} />
              <SkeletonLine w={`${w}px`} h={12} />
            </div>
          ))}
        </div>
        <div className="rounded-lg bg-black/30 p-3 h-44 space-y-2">
          {[85, 60, 70, 50, 65].map((w, i) => (
            <SkeletonLine key={i} w={`${w}%`} h={10} />
          ))}
        </div>
        <p className="text-xs text-gray-700 text-center mt-3 animate-pulse">Analyzing content…</p>
      </div>
    </div>
  );
}

// ─── Plan recommendation ──────────────────────────────────────────────────────

function PlanRecommendation({
  sitemap,
  isJsRendered,
}: {
  sitemap: SitemapData | null;
  isJsRendered: boolean;
}) {
  if (!sitemap) return null;
  const plan = sitemap.recommendedPlan;
  const label = PLAN_LABELS[plan] ?? plan;
  const price = sitemap.recommendedPrice ?? PLAN_PRICES[plan] ?? null;

  const smallSite = sitemap.totalPages <= 10;
  const coversCopy = smallSite
    ? "room to grow to 100 pages"
    : `covers all ${sitemap.totalPages.toLocaleString()} pages`;

  const perPage = price != null && price > 0 && sitemap && sitemap.totalPages > 0
    ? (price / sitemap.totalPages).toFixed(2)
    : null;

  return (
    <div className="mt-4 text-center">
      <p className="text-sm text-gray-500">
        Recommended:{" "}
        <span className="text-gray-300 font-medium">{label} plan</span>
        {price != null && price > 0 && <span className="text-gray-400"> · ${price}/mo</span>}
        {" "}· {coversCopy}
        {perPage && <span className="text-gray-600"> · ${perPage}/page</span>}
      </p>
      {isJsRendered && (
        <p className="text-xs text-amber-500/70 mt-1">
          Without Legible, AI crawlers see none of them.
        </p>
      )}
    </div>
  );
}

// ─── Deployment reassurance ───────────────────────────────────────────────────

function buildSignalChecks(llmChecks: LlmChecks, isJsRendered: boolean): SignalCheckItem[] {
  const terminologyDoc = "/docs/ai-content-terminology";
  const llmsTxtStatus: SignalStatus = llmChecks.llmsTxt.found
    ? llmChecks.llmsTxt.entryCount > 0 ? "ready" : "partial"
    : "missing";
  const llmsFullStatus: SignalStatus = llmChecks.llmsFullTxt.found ? "ready" : "missing";
  const aiSitemapStatus: SignalStatus = !llmChecks.aiSitemap.found
    ? "missing"
    : !llmChecks.aiSitemap.isValidJson || llmChecks.aiSitemap.itemCount === 0
      ? "partial"
      : "ready";
  const markdownStatus: SignalStatus = llmChecks.markdown.servesMarkdown ? "ready" : "missing";
  const varyAcceptStatus: SignalStatus = llmChecks.markdown.hasVaryAccept ? "ready" : "missing";
  const crawlerPolicyStatus: SignalStatus = llmChecks.robots.blockedCrawlerNames.length === 0 ? "ready" : "missing";
  const discoverySignalCount = Number(llmChecks.discovery.hasAlternateMarkdown) + Number(llmChecks.discovery.hasAiIndexLink);
  const discoveryStatus: SignalStatus = discoverySignalCount === 2 ? "ready" : discoverySignalCount === 1 ? "partial" : "missing";
  const pageIndexingStatus: SignalStatus = llmChecks.pagePolicy.blocksIndexing
    ? "missing"
    : llmChecks.pagePolicy.blocksFollowing
      ? "partial"
      : "ready";
  const freshnessSignalCount = Number(llmChecks.freshness.hasLastModified) + Number(llmChecks.freshness.hasEtag);
  const freshnessStatus: SignalStatus = freshnessSignalCount === 2 ? "ready" : freshnessSignalCount === 1 ? "partial" : "missing";
  const metadataSignalCount = Number(llmChecks.metadata.hasTitle) + Number(llmChecks.metadata.hasMetaDescription);
  const titleMetaStatus: SignalStatus = metadataSignalCount === 2 ? "ready" : metadataSignalCount === 1 ? "partial" : "missing";
  const responsePolicySignalCount = Number(llmChecks.markdown.hasContentSignal) + Number(llmChecks.markdown.hasXRobotsTag);
  const responsePolicyStatus: SignalStatus = responsePolicySignalCount === 2 ? "ready" : responsePolicySignalCount === 1 ? "partial" : "missing";

  return [
    {
      label: "llms.txt",
      status: llmsTxtStatus,
      detail: llmChecks.llmsTxt.found
        ? `Found at ${llmChecks.llmsTxt.path} with ${llmChecks.llmsTxt.entryCount} linked item${llmChecks.llmsTxt.entryCount === 1 ? "" : "s"}.`
        : "No machine-readable content index was found.",
      href: `${terminologyDoc}#llms-txt`,
    },
    {
      label: "llms-full.txt",
      status: llmsFullStatus,
      detail: llmChecks.llmsFullTxt.found
        ? `Found at ${llmChecks.llmsFullTxt.path} with ${llmChecks.llmsFullTxt.lineCount} content line${llmChecks.llmsFullTxt.lineCount === 1 ? "" : "s"}.`
        : "No full-document context file was detected.",
      href: "/docs/llms-full-txt",
    },
    {
      label: "ai-sitemap.json",
      status: aiSitemapStatus,
      detail: !llmChecks.aiSitemap.found
        ? "No AI-specific sitemap was detected."
        : !llmChecks.aiSitemap.isValidJson
          ? "Found, but it does not parse as valid JSON."
          : llmChecks.aiSitemap.itemCount === 0
            ? "Found and valid, but no indexed items were detected."
            : `Found with ${llmChecks.aiSitemap.itemCount} indexed item${llmChecks.aiSitemap.itemCount === 1 ? "" : "s"}.`,
      href: `${terminologyDoc}#ai-sitemap-json`,
    },
    {
      label: "Markdown delivery",
      status: markdownStatus,
      detail: llmChecks.markdown.servesMarkdown
        ? "The page returns clean Markdown when an LLM crawler asks for it."
        : "The page does not return clean Markdown when an LLM crawler asks for it.",
      href: `${terminologyDoc}#clean-markdown-delivery`,
    },
    {
      label: "Vary: Accept",
      status: varyAcceptStatus,
      detail: llmChecks.markdown.hasVaryAccept
        ? "The response advertises Vary: Accept so caches can distinguish Markdown from HTML."
        : "The response does not advertise Vary: Accept.",
      href: `${terminologyDoc}#vary-accept-headers`,
    },
    {
      label: "Crawler policy",
      status: crawlerPolicyStatus,
      detail: llmChecks.robots.blockedCrawlerNames.length === 0
        ? llmChecks.robots.found
          ? "We did not detect robots.txt blocks for the major AI crawlers we tested."
          : "No robots.txt blocks were detected for the major AI crawlers we tested."
        : `${llmChecks.robots.blockedCrawlerNames.join(", ")} are blocked in robots.txt.`,
      href: `${terminologyDoc}#robots-txt`,
    },
    {
      label: "HTML discovery links",
      status: discoveryStatus,
      detail: discoverySignalCount === 2
        ? "The page advertises both Markdown and AI-index discovery links."
        : discoverySignalCount === 1
          ? "The page advertises one discovery link, but not the full recommended set."
          : "No HTML discovery links point crawlers to Markdown or AI indexes.",
      href: `${terminologyDoc}#ai-meta-tags`,
    },
    {
      label: "Page indexing policy",
      status: pageIndexingStatus,
      detail: llmChecks.pagePolicy.blocksIndexing
        ? `Robots meta includes ${llmChecks.pagePolicy.robotsContent ?? "noindex"}.`
        : llmChecks.pagePolicy.blocksFollowing
          ? `Robots meta allows indexing but includes ${llmChecks.pagePolicy.robotsContent ?? "nofollow"}.`
        : llmChecks.pagePolicy.hasRobotsMeta
            ? `Robots meta is present and does not block indexing (${llmChecks.pagePolicy.robotsContent}).`
            : "No blocking noindex tag was detected at page level.",
      href: `${terminologyDoc}#page-level-robots-meta-tags`,
    },
    {
      label: "Freshness headers",
      status: freshnessStatus,
      detail: freshnessSignalCount === 2
        ? "Both Last-Modified and ETag are present on the page response."
        : freshnessSignalCount === 1
          ? `${llmChecks.freshness.hasLastModified ? "Last-Modified" : "ETag"} is present, but adding both would strengthen freshness signaling.`
          : "No Last-Modified or ETag headers were detected.",
      href: `${terminologyDoc}#freshness-headers-last-modified-and-etag`,
    },
    {
      label: "Canonical URL",
      status: llmChecks.metadata.hasCanonical ? "ready" : "missing",
      detail: llmChecks.metadata.hasCanonical
        ? "Canonical metadata is present."
        : "No canonical URL was detected on the page.",
      href: `${terminologyDoc}#canonical-urls-title-tags-and-meta-descriptions`,
    },
    {
      label: "Title & meta description",
      status: titleMetaStatus,
      detail: metadataSignalCount === 2
        ? "Title and meta description are both present."
        : metadataSignalCount === 1
          ? "Only one of title or meta description was detected."
          : "Neither title nor meta description was detected.",
      href: `${terminologyDoc}#canonical-urls-title-tags-and-meta-descriptions`,
    },
    {
      label: "JSON-LD structured data",
      status: llmChecks.structuredData.hasJsonLd ? "ready" : "missing",
      detail: llmChecks.structuredData.hasJsonLd
        ? `Structured data is present in ${llmChecks.structuredData.jsonLdCount} JSON-LD block${llmChecks.structuredData.jsonLdCount === 1 ? "" : "s"}.`
        : "No JSON-LD structured data detected on the page.",
      href: `${terminologyDoc}#json-ld-structured-data`,
    },
    {
      label: "Semantic main/article",
      status: llmChecks.semantics.hasMain || llmChecks.semantics.hasArticle ? "ready" : "missing",
      detail: llmChecks.semantics.hasMain || llmChecks.semantics.hasArticle
        ? "The page exposes a semantic main or article container."
        : "The page lacks a clear semantic main/article wrapper.",
      href: `${terminologyDoc}#semantic-html-structure`,
    },
    {
      label: "H1 hierarchy",
      status: llmChecks.semantics.hasH1 ? "ready" : "missing",
      detail: llmChecks.semantics.hasH1
        ? "The page has a visible H1 heading for content hierarchy."
        : "No H1 heading was detected on the analyzed page.",
      href: `${terminologyDoc}#heading-hierarchy-and-h1s`,
    },
    {
      label: "Response policy headers",
      status: responsePolicyStatus,
      detail: responsePolicySignalCount === 2
        ? "Both Content-Signal and X-Robots-Tag headers are present."
        : responsePolicySignalCount === 1
          ? "One response-level policy header is present, but not the full recommended set."
          : "No response-level AI policy headers were detected on the page response.",
      href: `${terminologyDoc}#content-signal-headers`,
    },
    {
      label: "JavaScript rendering risk",
      status: isJsRendered ? "missing" : "ready",
      detail: isJsRendered
        ? "The page appears to rely on JavaScript rendering, which many AI crawlers do not execute."
        : "The page exposes enough server-rendered content for crawlers to read without running JavaScript.",
      href: `${terminologyDoc}#javascript-rendering-and-ai-crawlers`,
    },
  ];
}

function DetailedSignalCheck({ label, status, detail, href }: SignalCheckItem) {
  const statusConfig = {
    ready: {
      label: "Ready",
      tone: "text-emerald-300 bg-emerald-500/10 border-emerald-400/20",
      icon: <CheckCircle className="w-3.5 h-3.5" />,
    },
    partial: {
      label: "Needs work",
      tone: "text-amber-300 bg-amber-500/10 border-amber-400/20",
      icon: <RotateCcw className="w-3.5 h-3.5" />,
    },
    missing: {
      label: "Missing",
      tone: "text-rose-300 bg-rose-500/10 border-rose-400/20",
      icon: <XCircle className="w-3.5 h-3.5" />,
    },
  }[status];

  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-4">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-white">{label}</span>
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${statusConfig.tone}`}>
          {statusConfig.icon}
          {statusConfig.label}
        </span>
      </div>
      <p className="mt-2 text-xs text-gray-400 leading-relaxed">{detail}</p>
      <Link
        href={href}
        className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-blue-300 hover:text-white transition-colors"
      >
        Learn more in docs
        <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}

function LlmReadinessPanel({
  llmChecks,
  isJsRendered,
  planUrl,
  sitemap,
  signalsNeedingWork,
  reductionPct,
}: {
  llmChecks: LlmChecks | null;
  isJsRendered: boolean;
  planUrl: string;
  sitemap: SitemapData | null;
  signalsNeedingWork: number;
  reductionPct: number | null;
}) {
  const [signalExpanded, setSignalExpanded] = useState(false);

  if (!llmChecks) return null;

  const hasJsShellRisk = llmChecks.summary.jsShellRisk || isJsRendered;
  const score = llmChecks.summary.score;
  const blockers = llmChecks.summary.findings;
  const strengths = llmChecks.summary.strengths;
  const signalChecks = buildSignalChecks(llmChecks, hasJsShellRisk);
  const scoreStyles = {
    poor: "text-rose-300 bg-rose-500/10 border-rose-400/20",
    "needs-work": "text-amber-300 bg-amber-500/10 border-amber-400/20",
    good: "text-lime-300 bg-lime-500/10 border-lime-400/20",
    strong: "text-emerald-300 bg-emerald-500/10 border-emerald-400/20",
  }[llmChecks.summary.scoreBand];

  // Softer score labels
  const scoreLabelMap: Record<string, string> = {
    poor: "Room to improve",
    "needs-work": "Getting there",
    good: "Looking good",
    strong: "Excellent",
  };
  const softScoreLabel = scoreLabelMap[llmChecks.summary.scoreBand] ?? llmChecks.summary.scoreLabel;

  return (
    <section className="rounded-2xl border border-white/[0.08] bg-gradient-to-b from-white/[0.06] to-transparent p-6 backdrop-blur-sm">
      {/* Score + inline early CTA */}
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-400 mb-2">AI visibility score</p>
          <h2 className="text-2xl font-bold text-white mb-2">How likely AI is to cite this page today</h2>
          <p className="text-sm text-gray-400 mb-4">
            AI systems don&apos;t browse like people. They look for specific signals — discovery files, Markdown delivery, structured data, freshness headers, semantic structure — to decide whether a page is worth citing. We checked all {llmChecks.summary.checksTotal} of them.
          </p>
          {/* Early CTA — right next to the score for users ready to act */}
          <div className="rounded-xl border border-blue-500/25 bg-gradient-to-r from-blue-500/10 to-blue-600/5 p-4">
            <p className="text-sm text-gray-300 mb-3">
              {hasJsShellRisk
                ? "Your content is locked behind JavaScript — AI crawlers can't read it at all. Legible renders it and delivers clean Markdown so your pages can actually be cited."
                : reductionPct
                  ? `${reductionPct}% of this page is noise AI has to wade through. Legible strips it to clean Markdown${signalsNeedingWork > 0 ? ` and fixes all ${signalsNeedingWork} missing signal${signalsNeedingWork === 1 ? "" : "s"}` : ""} — so crawlers can discover, read, and cite your content.`
                  : `Legible delivers your content as clean Markdown and fixes missing signals — so AI crawlers can discover, read, and cite your pages.`
              }
            </p>
            <div className="flex flex-wrap gap-3 items-center">
              <Link
                href={planUrl}
                className="px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-500 transition-all shadow-[0_0_15px_rgba(59,130,246,0.25)] hover:shadow-[0_0_25px_rgba(59,130,246,0.4)] inline-flex items-center gap-2"
              >
                Be the answer AI gives <ArrowRight className="w-4 h-4" />
              </Link>
              <span className="text-xs text-gray-500">Launch in minutes, not a dev sprint</span>
            </div>
          </div>
        </div>
        <div className="text-left lg:text-right shrink-0 lg:pl-6">
          <div className="relative inline-block">
            <div
              className="absolute -inset-4 rounded-full blur-2xl"
              style={{
                background: score >= 60
                  ? "rgba(16,185,129,0.35)"
                  : score >= 40
                    ? "rgba(245,158,11,0.35)"
                    : "rgba(244,63,94,0.35)",
              }}
            />
            <div className="relative text-6xl font-black tabular-nums text-white drop-shadow-lg">{score}</div>
          </div>
          <div className="text-sm text-gray-400 mt-1">out of 100</div>
          <div className={`mt-2 inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${scoreStyles}`}>
            {softScoreLabel}
          </div>
          <p className="mt-2 text-xs text-gray-500">
            {llmChecks.summary.checksPassed} of {llmChecks.summary.checksTotal} crawler signals in place
          </p>
          {score < 50 && (
            <p className="mt-1 text-xs text-gray-600">Most sites score under 50</p>
          )}
          {llmChecks.summary.blockedCrawlerNames.length > 0 && (
            <p className="mt-2 text-xs text-amber-300">
              Blocked: {llmChecks.summary.blockedCrawlerNames.join(", ")}
            </p>
          )}
        </div>
      </div>

      {/* Why AI isn't citing + what's already working */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        <div className="rounded-xl border border-amber-500/15 bg-amber-500/[0.03] p-4">
          <p className="text-sm font-semibold text-white mb-1">Why AI isn&apos;t citing this content</p>
          <p className="text-xs text-gray-500 mb-3">To fix manually, you&apos;d need 25–60 hours of developer work</p>
          {blockers.length > 0 ? (
            <ul className="space-y-2">
              {blockers.map((item) => (
                <li key={item} className="text-sm text-gray-300 flex items-start gap-2">
                  <XCircle className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-400">This page already exposes a strong set of AI-readability signals.</p>
          )}
          {blockers.length > 0 && (
            <p className="mt-3 text-xs text-gray-600">Plus ongoing maintenance every time your content changes — 3–8 hours per update cycle.</p>
          )}
        </div>

        <div className="rounded-xl border border-emerald-500/15 bg-emerald-500/[0.03] p-4">
          <p className="text-sm font-semibold text-white mb-1">What already looks good</p>
          <p className="text-xs text-gray-500 mb-3">Signals that are already helping AI find your content</p>
          {strengths.length > 0 ? (
            <ul className="space-y-2">
              {strengths.map((item) => (
                <li key={item} className="text-sm text-gray-300 flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-400">We found fewer positive signals than expected for AI crawlers on this page.</p>
          )}
          {hasJsShellRisk && (
            <p className="mt-4 text-xs text-amber-300">
              JavaScript shell risk: most AI crawlers can&apos;t execute JS, so they see an empty page instead of your content.
            </p>
          )}
        </div>
      </div>

      {/* Collapsible full signal breakdown */}
      <div className="mt-6 rounded-xl border border-white/[0.08] bg-white/[0.02]">
        <button
          onClick={() => setSignalExpanded(!signalExpanded)}
          className="w-full p-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between cursor-pointer hover:bg-white/5 rounded-xl transition-colors"
        >
          <div className="text-left">
            <p className="text-sm font-semibold text-white">Full signal breakdown</p>
            <p className="text-xs text-gray-500">Every signal contributing to the score is listed here.</p>
          </div>
          <div className="flex items-center gap-3">
            <p className="text-xs text-gray-500">
              {signalChecks.filter((item) => item.status === "ready").length} ready · {signalChecks.filter((item) => item.status === "partial").length} need work · {signalChecks.filter((item) => item.status === "missing").length} missing
            </p>
            {signalExpanded ? (
              <ChevronUp className="w-4 h-4 text-gray-500 shrink-0" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />
            )}
          </div>
        </button>
        {signalExpanded && (
          <div className="px-4 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {signalChecks.map((item) => (
                <DetailedSignalCheck key={item.label} {...item} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function PreviewClient() {
  const params = useSearchParams();
  const router = useRouter();
  const url = params?.get("url") ?? "";

  const [phase1, setPhase1] = useState<Phase1Data | null>(null);
  const [phase2, setPhase2] = useState<Phase2Data | null>(null);
  const [phase1Loading, setPhase1Loading] = useState(true);
  const [phase2Loading, setPhase2Loading] = useState(true);
  const [phase1Error, setPhase1Error] = useState<string | null>(null);
  const [phase2Error, setPhase2Error] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [shareStatus, setShareStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const hasFetched = useRef(false);

  // Derive display domain from the URL param immediately (before API responds)
  const displayDomain = (() => {
    try {
      return new URL(url).hostname;
    } catch { return url; }
  })();

  useEffect(() => {
    if (!url) return;
    if (hasFetched.current) return;
    hasFetched.current = true;

    // Phase 1 — fast: domain, sitemap, platform, NS
    fetch("/api/public/analyze-quick", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ url }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.error) throw new Error(data.error);
        setPhase1(data as Phase1Data);
      })
      .catch((err) => setPhase1Error((err as Error).message))
      .finally(() => setPhase1Loading(false));

    // Phase 2 — slow: CF crawl + markdown
    fetch("/api/public/analyze-crawl", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ url }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.error) setPhase2Error(data.error);
        else setPhase2(data as Phase2Data);
      })
      .catch((err) => setPhase2Error((err as Error).message))
      .finally(() => setPhase2Loading(false));
  }, [url]);

  // ── No URL: show standalone URL input ────────────────────────────────────────
  if (!url) {
    return (
      <div className="min-h-[80vh] bg-[#0F172A] flex flex-col items-center justify-center gap-8 px-4 text-center">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-400 mb-3">Free analysis</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            See what AI models see<br />on your site today
          </h1>
          <p className="text-gray-400 max-w-md mx-auto text-sm">
            Enter your website URL — we&apos;ll show you the raw HTML mess AI crawlers get,
            and what clean Markdown they&apos;d get with Legible.
          </p>
        </div>
        <UrlAnalyzerBar defaultUrl="" />
        <p className="text-xs text-gray-600">No account needed · Free forever for small sites</p>
      </div>
    );
  }

  // ── Phase 1 hard error ────────────────────────────────────────────────────────
  if (!phase1Loading && phase1Error) {
    return (
      <div className="min-h-[calc(100vh-5rem)] bg-[#0F172A] flex flex-col items-center justify-center gap-6 px-4 text-center max-w-lg mx-auto w-full">
        <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center">
          <XCircle className="w-7 h-7 text-red-400" />
        </div>
        <div>
          <p className="text-xl font-semibold text-white mb-2">Couldn&apos;t analyze that URL</p>
          <p className="text-gray-400 text-sm">{phase1Error}</p>
        </div>
        <UrlAnalyzerBar defaultUrl={url} />
        <Link href="/" className="text-sm text-gray-500 hover:text-gray-300 transition-colors">
          &larr; Back to homepage
        </Link>
      </div>
    );
  }

  // ── Derived values ─────────────────────────────────────────────────────────────
  const sitemap = phase1?.sitemap ?? null;
  const infrastructure = phase1?.infrastructure ?? null;
  const platform = phase1?.platform ?? null;
  const llmChecks = phase2?.llmChecks ?? phase1?.llmChecks ?? null;
  const deployPath = infrastructure?.deploymentPath ?? "byoc";
  const dnsProvider = infrastructure?.dnsProvider ?? "unknown";
  const cms = platform?.cms ?? "unknown";
  const pages = sitemap?.totalPages ?? 0;
  const actualDomain = phase1?.domain ?? displayDomain;
  const planUrl = `/onboarding/plan?url=${encodeURIComponent(url)}&path=${deployPath}&provider=${dnsProvider}&cms=${cms}&pages=${pages}`;
  const hasJsShellRisk = llmChecks?.summary.jsShellRisk ?? phase2?.isJsRendered ?? false;
  const detailedSignalChecks = llmChecks ? buildSignalChecks(llmChecks, hasJsShellRisk) : [];
  const signalsNeedingWork = detailedSignalChecks.filter((item) => item.status !== "ready").length;
  // CTA copy — tie everything back to the hero: "Be the answer AI gives"
  const reductionPct = phase2?.stats.reductionPercent ?? null;
  const htmlTokenCount = phase2?.stats.htmlTokens ?? null;
  const markdownTokenCount = phase2?.stats.markdownTokens ?? null;

  const ctaHeading = hasJsShellRisk
    ? "Your content is invisible to AI. Legible fixes that in minutes."
    : reductionPct
      ? `${reductionPct}% of this page is noise AI has to wade through. Legible strips it.`
      : "AI is drowning in your HTML. Legible gives it the clean version.";

  const ctaPrimaryCopy = hasJsShellRisk
    ? "Right now, AI crawlers see an empty JavaScript shell when they visit your site. No content, no citation. Legible renders your pages and delivers them as clean Markdown — so when ChatGPT, Claude, or Perplexity could cite you, you're actually in the running."
    : htmlTokenCount && markdownTokenCount
      ? `Your page sends ${htmlTokenCount.toLocaleString()} tokens of HTML to every AI crawler — but only ${markdownTokenCount.toLocaleString()} tokens are your actual content. The rest is layout, scripts, and navigation noise. Legible strips all of that and delivers clean Markdown with structured metadata, so AI systems reach your content immediately and cite it accurately.`
      : "Every AI crawler that visits your site wades through thousands of tokens of HTML noise to find your actual content. Legible strips all of that and delivers clean Markdown — so AI systems understand your content, cite it accurately, and source their answers from your site.";

  const ctaSignalsCopy = signalsNeedingWork > 0
    ? `Legible manages all ${llmChecks?.summary.checksTotal ?? 16} crawler signals${sitemap?.totalPages ? ` across ${sitemap.totalPages.toLocaleString()} pages` : ""} — discovery files, Markdown delivery, content negotiation, freshness, structured data, and more. It fixes the ${signalsNeedingWork} missing signal${signalsNeedingWork === 1 ? "" : "s"} found above and keeps everything in sync as your content changes.`
    : `Legible manages all ${llmChecks?.summary.checksTotal ?? 16} crawler signals${sitemap?.totalPages ? ` across ${sitemap.totalPages.toLocaleString()} pages` : ""} — discovery files, Markdown delivery, content negotiation, freshness, structured data, and more. Everything stays in sync as your content changes.`;

  const ctaButtonLabel = "Be the answer AI gives";

  // ── Main page (renders immediately, fills in progressively) ──────────────────
  return (
    <div className="min-h-[calc(100vh-5rem)] bg-[#0B1120] text-white">
      {/* Ambient background glow */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0" aria-hidden="true">
        <div className="absolute -top-40 left-[5%] w-[700px] h-[700px] rounded-full blur-[150px]" style={{ background: "rgba(59,130,246,0.12)" }} />
        <div className="absolute top-[40%] -right-20 w-[500px] h-[500px] rounded-full blur-[130px]" style={{ background: "rgba(99,102,241,0.09)" }} />
        <div className="absolute bottom-0 left-[25%] w-[500px] h-[500px] rounded-full blur-[130px]" style={{ background: "rgba(16,185,129,0.07)" }} />
      </div>

      <div className="relative max-w-5xl mx-auto px-4 py-10 space-y-10">

        {/* 1. DOMAIN HEADING + PURPOSE */}
        <div className="text-center pt-6 pb-2">
          {phase1?.url ? (
            <a
              href={phase1.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-3xl sm:text-4xl font-black text-white hover:text-blue-400 transition-colors inline-flex items-center gap-2 break-all"
            >
              {actualDomain}
              <ExternalLink className="w-6 h-6 shrink-0 text-gray-500" />
            </a>
          ) : (
            <span className="text-3xl sm:text-4xl font-black text-white inline-flex items-center gap-2">
              {displayDomain}
              <ExternalLink className="w-6 h-6 shrink-0 text-gray-500/30" />
            </span>
          )}
          <p className="mt-4 text-lg sm:text-xl text-gray-200 max-w-2xl mx-auto font-medium leading-snug">
            Can AI actually read{" "}
            <span className="bg-gradient-to-r from-blue-400 to-blue-300 bg-clip-text text-transparent font-bold">{actualDomain ?? displayDomain}</span>{" "}
            — or is it just skipping it?
          </p>
          <p className="mt-3 text-sm text-gray-400 max-w-xl mx-auto leading-relaxed">
            When someone asks ChatGPT, Claude, or Perplexity a question your site could answer, <strong className="text-gray-300">{llmChecks?.summary.checksTotal ?? 16} signals decide whether you get cited or ignored.</strong> We checked all of them.
          </p>
        </div>

        {/* 2. INFO BAR */}
        {phase1Loading ? (
          <InfoBarSkeleton />
        ) : phase1 ? (
          <InfoBar
            phase1={phase1}
            phase2={phase2}
            phase2Loading={phase2Loading}
            phase2Error={phase2Error}
          />
        ) : null}

        {/* 3. LLM READINESS (score + early CTA + opportunities + collapsible breakdown) */}
        {phase1 && (
          <LlmReadinessPanel
            llmChecks={llmChecks}
            isJsRendered={phase2?.isJsRendered ?? false}
            planUrl={planUrl}
            sitemap={sitemap}
            signalsNeedingWork={signalsNeedingWork}
            reductionPct={reductionPct}
          />
        )}

        {/* 4. COMPARISON SECTION — proof of what changes */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-400/70 text-center mb-1">
            The difference
          </p>
          <p className="text-center text-sm text-gray-400 mb-5 max-w-lg mx-auto">
            Same content. Completely different outcome. On the left: what AI crawlers process today. On the right: what they&apos;d get with Legible. AI struggles to understand the first — and cites the second.
          </p>

          {/* Skeleton while crawl is in progress */}
          {phase2Loading && !phase2 && <ComparisonSkeleton />}

          {/* Graceful error state */}
          {!phase2Loading && phase2Error && !phase2 && (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center">
              <p className="text-gray-400 text-sm">
                We couldn&apos;t generate a full preview for this site right now.
              </p>
              <p className="text-gray-600 text-xs mt-2">
                You can still get started — Legible works with any site.
              </p>
            </div>
          )}

          {/* ── MODE A: Traditional HTML site ── */}
          {phase2 && !phase2.isJsRendered && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* What AI sees today — raw HTML */}
              <div className="rounded-2xl border border-red-500/20 bg-red-500/5 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 rounded-full bg-red-400" />
                  <span className="text-sm font-semibold text-red-400 uppercase tracking-wider">What AI sees today</span>
                </div>
                <div className="space-y-3 mb-5">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Tokens wasted</span>
                    <span className="font-mono text-red-300">{phase2.stats.htmlTokens.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Format</span>
                    <span className="text-gray-300">Raw HTML</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Structure</span>
                    <span className="flex items-center gap-1 text-red-400">
                      <XCircle className="w-3.5 h-3.5" /> None
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Layout noise</span>
                    <span className="text-red-300">~{Math.min(phase2.stats.reductionPercent + 5, 95)}%</span>
                  </div>
                </div>
                <div className="rounded-lg bg-black/30 p-3 overflow-auto max-h-48 font-mono text-xs text-gray-500 leading-relaxed">
                  {`<html><head><script>/* analytics */</script>\n<style>/* 2000 lines of CSS */</style>\n</head><body>\n<nav class="nav-wrapper...">...</nav>\n<div class="container flex...">\n  <aside class="sidebar">...</aside>\n  <!-- actual content buried here -->`}
                </div>
              </div>

              {/* With Legible — clean Markdown */}
              <div className="rounded-2xl border border-blue-500/20 bg-blue-500/5 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 rounded-full bg-blue-400" />
                  <span className="text-sm font-semibold text-blue-400 uppercase tracking-wider">With Legible</span>
                </div>
                <div className="space-y-3 mb-5">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Tokens</span>
                    <span className="font-mono text-blue-300">{phase2.stats.markdownTokens.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Format</span>
                    <span className="text-gray-300">Clean Markdown</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Structure</span>
                    <span className="flex items-center gap-1 text-blue-400">
                      <CheckCircle className="w-3.5 h-3.5" /> Metadata + headings
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Layout noise</span>
                    <span className="text-blue-300">0%</span>
                  </div>
                </div>
                <div className="rounded-lg bg-black/30 p-3 overflow-auto max-h-48 font-mono text-xs text-blue-200 leading-relaxed whitespace-pre-wrap">
                  {phase2.markdown.slice(0, 500) ||
                    "# Article Title\n\nClean, structured Markdown content ready for AI systems.\n\n## Key points\n- Semantic structure preserved\n- All noise removed\n- Optimized for token efficiency"}
                </div>
                {(phase2.markdownFull || phase2.markdown) && (
                  <button
                    onClick={() => {
                      const content = phase2.markdownFull ?? phase2.markdown;
                      const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
                      const blobUrl = URL.createObjectURL(blob);
                      window.open(blobUrl, "_blank");
                      setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
                    }}
                    className="mt-3 w-full text-xs text-blue-400/70 hover:text-blue-300 transition-colors text-center py-1 border border-blue-500/15 hover:border-blue-500/30 rounded-md"
                  >
                    View full Markdown ↗
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── MODE B: JS-rendered / SPA site ── */}
          {phase2 && phase2.isJsRendered && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* What AI sees today — empty shell */}
              <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 rounded-full bg-amber-400" />
                  <span className="text-sm font-semibold text-amber-400 uppercase tracking-wider">What AI sees today</span>
                </div>
                <div className="space-y-3 mb-5">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Content</span>
                    <span className="flex items-center gap-1 text-amber-400">
                      <XCircle className="w-3.5 h-3.5" /> Almost none
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Format</span>
                    <span className="text-gray-300">Empty JS shell</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Structure</span>
                    <span className="flex items-center gap-1 text-amber-400">
                      <XCircle className="w-3.5 h-3.5" /> None
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Readable by AI</span>
                    <span className="flex items-center gap-1 text-amber-400">
                      <XCircle className="w-3.5 h-3.5" /> No
                    </span>
                  </div>
                </div>
                <div className="rounded-lg bg-black/30 p-3 overflow-auto max-h-48 font-mono text-xs text-amber-200/50 leading-relaxed">
                  {`<!DOCTYPE html>\n<html>\n  <head>\n    <script src="/bundle.js"></script>\n  </head>\n  <body>\n    <div id="root"></div>\n    <!-- nothing here until JS runs -->\n  </body>\n</html>`}
                </div>
              </div>

              {/* With Legible — fully rendered Markdown */}
              <div className="rounded-2xl border border-blue-500/20 bg-blue-500/5 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 rounded-full bg-blue-400" />
                  <span className="text-sm font-semibold text-blue-400 uppercase tracking-wider">With Legible</span>
                </div>
                <div className="space-y-3 mb-5">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Content</span>
                    <span className="flex items-center gap-1 text-blue-400">
                      <CheckCircle className="w-3.5 h-3.5" /> Full page
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Format</span>
                    <span className="text-gray-300">Clean Markdown</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Structure</span>
                    <span className="flex items-center gap-1 text-blue-400">
                      <CheckCircle className="w-3.5 h-3.5" /> Metadata + headings
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Readable by AI</span>
                    <span className="flex items-center gap-1 text-blue-400">
                      <CheckCircle className="w-3.5 h-3.5" /> Yes
                    </span>
                  </div>
                </div>
                <div className="rounded-lg bg-black/30 p-3 overflow-auto max-h-48 font-mono text-xs text-blue-200 leading-relaxed whitespace-pre-wrap">
                  {phase2.markdown.slice(0, 500) ||
                    "# Page Title\n\nFull rendered content, ready for AI.\n\n## Section\n- All text visible\n- Headings preserved\n- Clean structure"}
                </div>
                {(phase2.markdownFull || phase2.markdown) && (
                  <button
                    onClick={() => {
                      const content = phase2.markdownFull ?? phase2.markdown;
                      const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
                      const blobUrl = URL.createObjectURL(blob);
                      window.open(blobUrl, "_blank");
                      setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
                    }}
                    className="mt-3 w-full text-xs text-blue-400/70 hover:text-blue-300 transition-colors text-center py-1 border border-blue-500/15 hover:border-blue-500/30 rounded-md"
                  >
                    View full Markdown ↗
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 5. WHAT'S BLOCKING YOU — manual effort contrast */}
        {signalsNeedingWork > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-4xl mx-auto">
            {/* Left: what you'd need to fix */}
            <div className="rounded-2xl border border-amber-500/20 bg-amber-500/[0.04] p-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-amber-400/70 mb-2">What&apos;s blocking you</p>
              <h3 className="text-lg font-bold text-white mb-3">To fix this manually, you&apos;d need to:</h3>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-start gap-2"><span className="text-amber-400 shrink-0 mt-0.5">•</span>Create and maintain <strong className="text-white">llms.txt</strong></li>
                <li className="flex items-start gap-2"><span className="text-amber-400 shrink-0 mt-0.5">•</span>Generate <strong className="text-white">llms-full.txt</strong></li>
                <li className="flex items-start gap-2"><span className="text-amber-400 shrink-0 mt-0.5">•</span>Build an <strong className="text-white">ai-sitemap.json</strong></li>
                <li className="flex items-start gap-2"><span className="text-amber-400 shrink-0 mt-0.5">•</span>Serve <strong className="text-white">clean Markdown versions</strong> of every page</li>
                <li className="flex items-start gap-2"><span className="text-amber-400 shrink-0 mt-0.5">•</span>Implement <strong className="text-white">content negotiation</strong> (text/markdown responses)</li>
                <li className="flex items-start gap-2"><span className="text-amber-400 shrink-0 mt-0.5">•</span>Fix <strong className="text-white">semantic structure</strong> (H1, hierarchy, containers)</li>
                <li className="flex items-start gap-2"><span className="text-amber-400 shrink-0 mt-0.5">•</span>Add <strong className="text-white">AI discovery links</strong> across your site</li>
                <li className="flex items-start gap-2"><span className="text-amber-400 shrink-0 mt-0.5">•</span>Ensure <strong className="text-white">freshness headers and response policies</strong></li>
                <li className="flex items-start gap-2"><span className="text-amber-400 shrink-0 mt-0.5">•</span>Maintain <strong className="text-white">structured data consistency</strong></li>
              </ul>
            </div>

            {/* Right: estimated effort */}
            <div className="flex flex-col gap-5">
              <div className="rounded-2xl border border-red-500/20 bg-red-500/[0.04] p-6 flex-1">
                <p className="text-xs font-semibold uppercase tracking-widest text-red-400/70 mb-2">Estimated manual effort</p>
                <h3 className="text-lg font-bold text-white mb-3">Initial setup</h3>
                <div className="text-3xl font-black text-red-400 mb-3">25–60 hours</div>
                <div className="space-y-1.5 text-sm text-gray-400">
                  <div className="flex justify-between"><span>Discovery files</span><span className="text-gray-300">6–10h</span></div>
                  <div className="flex justify-between"><span>Markdown pipeline</span><span className="text-gray-300">10–25h</span></div>
                  <div className="flex justify-between"><span>AI crawl compatibility</span><span className="text-gray-300">5–15h</span></div>
                  <div className="flex justify-between"><span>Testing across AI systems</span><span className="text-gray-300">4–10h</span></div>
                </div>
              </div>

              <div className="rounded-2xl border border-red-500/20 bg-red-500/[0.04] p-6">
                <h3 className="text-base font-bold text-white mb-1">Ongoing maintenance</h3>
                <p className="text-sm text-gray-400 mb-2">Every time you update your site:</p>
                <div className="text-2xl font-black text-red-400 mb-2">3–8 hours</div>
                <p className="text-xs text-gray-500">per content update cycle — regenerate Markdown, update discovery files, sync structured data, revalidate crawl behavior</p>
              </div>
            </div>
          </div>
        )}

        {/* 5b. OR… TURN THIS ON IN MINUTES */}
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs font-semibold uppercase tracking-widest text-emerald-400/70 mb-2">Or&hellip; turn this on in minutes</p>
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">Legible fixes all of this automatically</h3>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <span className="px-4 py-2 rounded-full border border-emerald-500/20 bg-emerald-500/[0.06] text-emerald-300 font-medium">Clean Markdown for every page</span>
            <span className="px-4 py-2 rounded-full border border-emerald-500/20 bg-emerald-500/[0.06] text-emerald-300 font-medium">All {llmChecks?.summary.checksTotal ?? 16} signals maintained</span>
            <span className="px-4 py-2 rounded-full border border-emerald-500/20 bg-emerald-500/[0.06] text-emerald-300 font-medium">No developer involvement</span>
          </div>
        </div>

        {/* 5c. THIS ISN'T SEO */}
        <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6 max-w-3xl mx-auto">
          <div className="flex flex-col sm:flex-row gap-6 items-start">
            <div className="flex-1">
              <p className="text-xs font-semibold uppercase tracking-widest text-blue-400/70 mb-2">This isn&apos;t SEO</p>
              <h3 className="text-lg font-bold text-white mb-3">It&apos;s AI infrastructure</h3>
              <p className="text-sm text-gray-400 leading-relaxed">
                Without Legible, you&apos;d manage discovery systems, content delivery formats, crawl compatibility, freshness &amp; caching, and cross-model behavior yourself — and keep it all in sync.
              </p>
            </div>
            <div className="sm:border-l sm:border-white/10 sm:pl-6 shrink-0">
              <p className="text-sm text-gray-500 mb-1">With Legible:</p>
              <p className="text-xl font-bold text-emerald-400">It&apos;s handled automatically.</p>
              <p className="text-xs text-gray-500 mt-1">From <span className="text-white font-semibold">$19/month</span> — covers your entire site</p>
            </div>
          </div>
        </div>

        {/* 6. FINAL CTA — hero narrative + proof + Connect + share */}
        <div className="relative overflow-hidden rounded-2xl border border-blue-500/25 p-8" style={{ background: "linear-gradient(135deg, rgba(30,58,138,0.5) 0%, rgba(11,17,32,0.95) 40%, rgba(49,46,129,0.3) 100%)" }}>
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[350px] rounded-full blur-[120px]" style={{ background: "rgba(59,130,246,0.15)" }} />
            <div className="absolute bottom-0 right-0 w-[400px] h-[250px] rounded-full blur-[100px]" style={{ background: "rgba(99,102,241,0.1)" }} />
          </div>
          <div className="relative text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-blue-400 mb-3">Be the answer AI gives</p>
            <h2 className="text-2xl sm:text-3xl font-black text-white mb-4 leading-tight">
              {ctaHeading}
            </h2>
            <p className="text-gray-300 mb-4 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">
              {ctaPrimaryCopy}
            </p>
            <p className="text-gray-400 mb-8 max-w-2xl mx-auto text-sm leading-relaxed">
              {ctaSignalsCopy}
            </p>
          </div>

          {/* Four value-prop cards: what Legible does */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8 max-w-4xl mx-auto">

            {/* Card 1: Markdown */}
            <div className="relative overflow-hidden rounded-2xl border border-blue-500/30 bg-gradient-to-br from-blue-500/10 via-blue-600/5 to-transparent p-6 text-left group hover:border-blue-400/50 transition-colors">
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-full blur-2xl -translate-y-8 translate-x-8" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-blue-400" />
                  </div>
                  <h3 className="text-base font-bold text-white">Clean Markdown delivery</h3>
                </div>
                {reductionPct ? (
                  <div className="mb-3">
                    <span className="text-3xl font-black text-blue-400">{reductionPct}%</span>
                    <span className="text-sm text-gray-400 ml-2">less noise</span>
                  </div>
                ) : null}
                <p className="text-sm text-gray-300 leading-relaxed">
                  {reductionPct
                    ? `Every page gets a Markdown endpoint. Instead of ${htmlTokenCount?.toLocaleString() ?? "tens of thousands of"} tokens of HTML, crawlers get ${markdownTokenCount?.toLocaleString() ?? "just the content"} clean tokens — your words, not your layout code.`
                    : "Every page gets a Markdown endpoint — clean content with structured metadata, no HTML noise. AI systems read, understand, and cite your pages without wading through layout code."
                  }
                </p>
              </div>
            </div>

            {/* Card 2: Signals */}
            <div className="relative overflow-hidden rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-amber-600/5 to-transparent p-6 text-left group hover:border-amber-400/50 transition-colors">
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full blur-2xl -translate-y-8 translate-x-8" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <Settings className="w-5 h-5 text-amber-400" />
                  </div>
                  <h3 className="text-base font-bold text-white">{llmChecks?.summary.checksTotal ?? 16} signals, auto-managed</h3>
                </div>
                {signalsNeedingWork > 0 ? (
                  <div className="mb-3">
                    <span className="text-3xl font-black text-amber-400">{signalsNeedingWork}</span>
                    <span className="text-sm text-gray-400 ml-2">missing — Legible fixes all of them</span>
                  </div>
                ) : (
                  <div className="mb-3">
                    <span className="text-3xl font-black text-emerald-400">All set</span>
                    <span className="text-sm text-gray-400 ml-2">— Legible keeps them current</span>
                  </div>
                )}
                <p className="text-sm text-gray-300 leading-relaxed mb-2">
                  llms.txt, llms-full.txt, ai-sitemap.json, Vary:&nbsp;Accept, discovery links, freshness headers, response policies, JSON-LD, and more{sitemap?.totalPages ? ` across all ${sitemap.totalPages.toLocaleString()} pages` : ""}.
                </p>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Everything stays in sync as your content changes — no developer involvement needed.
                </p>
              </div>
            </div>

            {/* Card 3: Analytics */}
            <div className="relative overflow-hidden rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 via-emerald-600/5 to-transparent p-6 text-left group hover:border-emerald-400/50 transition-colors">
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-2xl -translate-y-8 translate-x-8" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-emerald-400" />
                  </div>
                  <h3 className="text-base font-bold text-white">AI crawler analytics</h3>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {["ChatGPT", "Claude", "Perplexity", "Gemini"].map((name) => (
                    <span key={name} className="px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/20 text-xs font-medium text-emerald-300">{name}</span>
                  ))}
                  <span className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-gray-400">+ more</span>
                </div>
                <p className="text-sm text-gray-300 leading-relaxed">
                  See exactly which AI systems visit, how many tokens they consume, which pages they read most, and how your cache is performing. Know when you&apos;re being cited, not just crawled.
                </p>
              </div>
            </div>

            {/* Card 4: Connect */}
            <div className="relative overflow-hidden rounded-2xl border border-indigo-500/30 bg-gradient-to-br from-indigo-500/10 via-indigo-600/5 to-transparent p-6 text-left group hover:border-indigo-400/50 transition-colors">
              <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 rounded-full blur-2xl -translate-y-8 translate-x-8" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                    <Plug className="w-5 h-5 text-indigo-400" />
                  </div>
                  <h3 className="text-base font-bold text-white">Legible Connect</h3>
                </div>
                <p className="text-xs text-indigo-300/70 font-mono mb-3 tracking-tight">mcp.getlegible.com/your-workspace</p>
                <p className="text-sm text-gray-300 leading-relaxed mb-3">
                  Give Claude, Codex, Replit, and other AI tools live access to your content via one MCP endpoint. Stop re-pasting context — let AI pull what it needs directly.
                </p>
                <Link
                  href="/connect/"
                  className="text-sm font-medium text-indigo-300 hover:text-indigo-200 transition-colors inline-flex items-center gap-1.5"
                >
                  Learn more <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>

          </div>

          {/* Proof: what this means in practice */}
          <div className="relative rounded-xl border border-white/[0.08] bg-white/[0.03] p-6 mb-8 max-w-4xl mx-auto overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/[0.03] via-transparent to-emerald-500/[0.03]" />
            <p className="relative text-xs font-semibold uppercase tracking-widest text-blue-400/70 mb-4">What changes after setup</p>
            <div className="relative grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-3xl font-black text-blue-400">{reductionPct ?? ">90"}%</div>
                <div className="text-xs text-gray-400 mt-1">less noise per page</div>
              </div>
              <div>
                <div className="text-3xl font-black text-amber-400">{llmChecks?.summary.checksTotal ?? 16}/{llmChecks?.summary.checksTotal ?? 16}</div>
                <div className="text-xs text-gray-400 mt-1">signals maintained</div>
              </div>
              <div>
                <div className="text-3xl font-black text-emerald-400">Live</div>
                <div className="text-xs text-gray-400 mt-1">crawler analytics</div>
              </div>
              <div>
                <div className="text-3xl font-black text-indigo-400">&lt; 5 min</div>
                <div className="text-xs text-gray-400 mt-1">to launch</div>
              </div>
            </div>
          </div>

          <div className="relative text-center">
            <div className="flex flex-col sm:flex-row justify-center items-center gap-3">
              <Link
                href={planUrl}
                className="relative px-8 py-4 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-500 transition-all shadow-[0_0_20px_rgba(59,130,246,0.3)] hover:shadow-[0_0_40px_rgba(59,130,246,0.5)] inline-flex items-center justify-center gap-2 text-base"
              >
                Fix my AI visibility <ArrowRight className="w-4 h-4" />
              </Link>
              <button
                onClick={() => setShareOpen(true)}
                className="px-7 py-3.5 border border-white/20 text-gray-300 font-semibold rounded-lg hover:bg-white/5 hover:border-white/30 transition-colors inline-flex items-center justify-center gap-2"
              >
                <Mail className="w-4 h-4" /> Share this report
              </button>
            </div>

            {/* Inline email input — expands below the buttons */}
            {shareOpen && (
              <div className="mt-5 max-w-md mx-auto">
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!shareEmail.trim() || !shareEmail.includes("@")) return;
                    setShareStatus("sending");
                    try {
                      const resp = await fetch("/api/public/share-report", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                          email: shareEmail.trim(),
                          domain: actualDomain,
                          url,
                          score: llmChecks?.summary.score,
                          checksPassed: llmChecks?.summary.checksPassed,
                          checksTotal: llmChecks?.summary.checksTotal,
                          findings: llmChecks?.summary.findings,
                          strengths: llmChecks?.summary.strengths,
                          reductionPct,
                          htmlTokens: htmlTokenCount,
                          markdownTokens: markdownTokenCount,
                          signalsNeedingWork,
                          totalPages: sitemap?.totalPages ?? null,
                        }),
                      });
                      if (!resp.ok) {
                        const data = await resp.json().catch(() => ({}));
                        throw new Error((data as { error?: string }).error ?? "Send failed");
                      }
                      setShareStatus("sent");
                    } catch (err) {
                      setShareStatus("error");
                      console.error("[share-report]", err);
                    }
                  }}
                  className="flex gap-2 flex-col sm:flex-row"
                >
                  <input
                    type="email"
                    value={shareEmail}
                    onChange={(e) => { setShareEmail(e.target.value); if (shareStatus === "error") setShareStatus("idle"); }}
                    placeholder="colleague@company.com"
                    className="flex-1 px-4 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={shareStatus === "sending" || shareStatus === "sent"}
                  />
                  <button
                    type="submit"
                    disabled={shareStatus === "sending" || shareStatus === "sent"}
                    className="px-5 py-2.5 bg-white/10 border border-white/20 text-white text-sm font-medium rounded-lg hover:bg-white/15 transition-colors disabled:opacity-50 whitespace-nowrap"
                  >
                    {shareStatus === "sending" ? "Sending…" : shareStatus === "sent" ? "Sent!" : "Send report"}
                  </button>
                </form>
                {shareStatus === "sent" && (
                  <p className="text-xs text-emerald-400 mt-2">Report sent to {shareEmail}</p>
                )}
                {shareStatus === "error" && (
                  <p className="text-xs text-red-400 mt-2">Something went wrong — please try again.</p>
                )}
              </div>
            )}

            <p className="text-gray-500 text-xs mt-4">No developer project required. One DNS record needed.</p>
            {phase1 && infrastructure && !infrastructure.isCloudflare && (
              <p className="flex items-center justify-center gap-2 text-xs text-gray-500 mt-2">
                <CheckCircle className="w-3.5 h-3.5 text-green-400 shrink-0" />
                One DNS record needed after signup — we&apos;ll guide you through it
              </p>
            )}
            <PlanRecommendation sitemap={sitemap} isJsRendered={phase2?.isJsRendered ?? false} />
          </div>
        </div>

        {/* 6. TRY ANOTHER URL */}
        <div className="mt-10 pb-6">
          <p className="text-center text-gray-400 text-sm mb-4 flex items-center justify-center gap-2">
            <RotateCcw className="w-3.5 h-3.5" /> Try another URL
          </p>
          <UrlAnalyzerBar defaultUrl="" />
        </div>

      </div>
    </div>
  );
}
